import { useLazyQuery, useQuery } from '@apollo/client';
import { useEffect } from 'react';

import HandleQuery from '@components/HandleQuery';
import InitialConsultReportEdit from '@components/InitialConsultReportEdit';
import ExpertJourneyPage from '@components/pageTypes/ExpertJourneyPage';
import useSubscribeToConsult from '@hooks/subscriptions/useSubscribeToConsult';
import useSubscribeToJourney from '@hooks/subscriptions/useSubscribeToJourney';
import {
  GetConsultDocument,
  GetConsultQuery,
  GetConsultQueryVariables,
  GetJourneyWithInitialConsultDocument,
  GetJourneyWithInitialConsultQuery,
  GetJourneyWithInitialConsultQueryVariables
} from '@typing/Generated';

type Props = {
  consultId: string | undefined;
  journeyId: string;
};

const ExpertJourneyInitialConsultReportEditPage = ({ consultId, journeyId }: Props) => {
  const [getConsult, getConsultQuery] = useLazyQuery<GetConsultQuery, GetConsultQueryVariables>(GetConsultDocument);

  useSubscribeToConsult(
    consultId ?? '',
    () => {
      getConsultQuery.refetch();
    },
    {
      skip: !consultId
    }
  );

  const getJourneyQuery = useQuery<GetJourneyWithInitialConsultQuery, GetJourneyWithInitialConsultQueryVariables>(
    GetJourneyWithInitialConsultDocument,
    {
      variables: { id: journeyId }
    }
  );

  useEffect(() => {
    const reportConsultId = getJourneyQuery.data?.journey.initialConsultReport.consult?.id ?? consultId;
    if (reportConsultId) {
      getConsult({
        variables: {
          id: reportConsultId
        }
      });
    }
  }, [consultId, getConsult, getJourneyQuery.data?.journey.initialConsultReport.consult?.id]);

  useSubscribeToJourney(journeyId, () => {
    getJourneyQuery.refetch();
  });

  return (
    <ExpertJourneyPage
      gradient={false}
      helmetKey="pageTitles.expertJourney.initialConsultReport.edit"
      journeyId={journeyId}
    >
      <HandleQuery query={getJourneyQuery}>
        {journeyData => (
          <InitialConsultReportEdit consult={getConsultQuery.data?.consult} journey={journeyData.journey} />
        )}
      </HandleQuery>
    </ExpertJourneyPage>
  );
};

export default ExpertJourneyInitialConsultReportEditPage;
